import { useConnection } from "@solana/wallet-adapter-react";
import type { PublicKey } from "@solana/web3.js";
import type { LiquidityPools, LiquidityPoolsData, StepLiquidityPool } from "@stepfinance/liquidity";
import type { NormalizedTokenAccount } from "@stepfinance/tokens";
import type { FC } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import type { MarinadeState } from "../hooks";
import { useMarinade } from "../hooks";
import { useLiquidityPoolsQuery } from "../hooks/queries/liquidity/useLiquidityPoolsQuery";
import { useAccountsContext } from "./accounts";

type LiquidityContextState = LiquidityPoolsData & {
  marinadeState?: MarinadeState;
  refreshing: boolean;
  refreshPools: () => Promise<void>;
  setLiquidityBalance: (balance: number) => void;
  liquidityBalance: number;
};

const initializeLiquidityPoolsData = (): LiquidityPoolsData => ({
  orcaPools: {},
  raydiumPools: {},
  saberPools: {},
  stepFinancePools: {},
  marinadePools: {},
  mercurialPools: {},
  atrixPools: {},
  aldrinPools: {},
  sarosPools: {},
});

const LiquidityContext = createContext<LiquidityContextState>({
  ...initializeLiquidityPoolsData(),
  refreshing: false,
  refreshPools: Promise.resolve,
  setLiquidityBalance: () => {},
  liquidityBalance: 0,
});

export const LiquidityProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [liquidityBalance, setLiquidityBalance] = useState(0);

  const marinadeState = useMarinade();

  const { data, refetch } = useLiquidityPoolsQuery();

  const pools = useMemo<LiquidityPoolsData>(() => {
    const liquidityPoolsData: LiquidityPoolsData = {
      orcaPools: {},
      raydiumPools: {},
      saberPools: {},
      stepFinancePools: {},
      marinadePools: {},
      mercurialPools: {},
      atrixPools: {},
      aldrinPools: {},
      sarosPools: {},
    };
    if (!data) {
      return liquidityPoolsData;
    }

    return data;
  }, [data]);

  const [refreshing, setRefreshing] = useState(false);
  const refreshPools = useCallback(async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  }, [refetch]);

  return (
    <LiquidityContext.Provider
      value={{
        ...pools,
        marinadeState,
        refreshing,
        refreshPools,
        setLiquidityBalance,
        liquidityBalance,
      }}
    >
      {children}
    </LiquidityContext.Provider>
  );
};

export const useLiquidity = () => {
  return useContext(LiquidityContext);
};

export const useLiquidityPools = (): LiquidityPools[] => {
  const { ...pools } = useContext(LiquidityContext);
  return Object.values(pools).flatMap<LiquidityPools>(Object.values);
};

export type OwnedPool = {
  account: NormalizedTokenAccount;
  pool: LiquidityPools;
};

/**
 * Returns an array of pool liquidity positions that the user owns across all userAccounts,
 * across all supported pools. E.g. ORCA-SOL on Orca, USDC-STEP on Step Finance
 *
 * Useful for displaying owned LP information back to the user.
 *
 * Note: Can contain duplicates. Utilize OwnedPool.pool.mintAddress to track uniqueness
 */
export const useOwnedPools = (): OwnedPool[] => {
  const { orcaPools, stepFinancePools, raydiumPools, saberPools, marinadePools, atrixPools, aldrinPools, sarosPools } =
    useLiquidity();
  const { userTokenAccounts } = useAccountsContext();
  return useMemo(() => {
    const map = userTokenAccounts.reduce((acc, item) => {
      const key = item.info.mint;
      acc.set(key, [...(acc.get(key) || []), item]);
      return acc;
    }, new Map<string, NormalizedTokenAccount[]>());
    let ownedPools: OwnedPool[] = [];
    for (const [key, account] of map.entries()) {
      let pool: LiquidityPools | undefined;
      if (Object.prototype.hasOwnProperty.call(orcaPools, key)) {
        pool = orcaPools[key]!;
      }
      if (Object.prototype.hasOwnProperty.call(stepFinancePools, key)) {
        pool = stepFinancePools[key]!;
      }
      if (Object.prototype.hasOwnProperty.call(saberPools, key)) {
        pool = saberPools[key]!;
      }
      if (Object.prototype.hasOwnProperty.call(raydiumPools, key)) {
        pool = raydiumPools[key]!;
      }
      if (Object.prototype.hasOwnProperty.call(marinadePools, key)) {
        pool = marinadePools[key]!;
      }
      if (Object.prototype.hasOwnProperty.call(atrixPools, key)) {
        pool = atrixPools[key]!;
      }
      if (Object.prototype.hasOwnProperty.call(aldrinPools, key)) {
        pool = aldrinPools[key]!;
      }
      if (Object.prototype.hasOwnProperty.call(sarosPools, key)) {
        pool = sarosPools[key]!;
      }
      if (pool) {
        const userAccounts = account.map<OwnedPool>((a) => ({
          account: a,
          pool: pool!,
        }));
        ownedPools = ownedPools.concat(userAccounts);
      }
    }
    return ownedPools;
  }, [
    userTokenAccounts,
    orcaPools,
    stepFinancePools,
    saberPools,
    raydiumPools,
    marinadePools,
    atrixPools,
    aldrinPools,
    sarosPools,
  ]);
};

export const usePoolsForBasket = (
  mints: [PublicKey | undefined, PublicKey | undefined],
): StepLiquidityPool[] | undefined => {
  const { connection } = useConnection();
  const { stepFinancePools } = useLiquidity();
  const [pools, setPools] = useState<StepLiquidityPool[]>();

  const [mintA, mintB] = mints;

  useEffect(() => {
    (() => {
      if (!mintA || !mintB) {
        return;
      }

      const potentialPools = Object.values(stepFinancePools);

      // reset pool during query
      setPools(undefined);
      const exactMatch = potentialPools.filter((p) => {
        const poolMintA = p.mints[0];
        const poolMintB = p.mints[1];
        return (
          (poolMintA === mintA.toBase58() && poolMintB === mintB.toBase58()) ||
          (poolMintA === mintB.toBase58() && poolMintB === mintA.toBase58())
        );
      });

      if (exactMatch.length === 1) {
        setPools(exactMatch);
        return;
      }

      const poolA = potentialPools.find((p) => p.mints[0] === mintA.toBase58());
      const poolB = potentialPools.find((p) => p.mints[0] === mintB.toBase58());

      if (!poolA || !poolB) {
        return;
      }

      const matchingPools = [poolA, poolB];

      setPools(matchingPools);

      return;
    })();
  }, [connection, mintA, mintB, stepFinancePools]);

  const sortPoolsForRoute = (a: StepLiquidityPool, b: StepLiquidityPool) => {
    const poolMintsA = a.mints;
    const poolMintsB = b.mints;
    const aFound = poolMintsA.includes(mintA?.toBase58() ?? "") ? 1 : 0;
    const bFound = poolMintsB.includes(mintB?.toBase58() ?? "") ? 1 : 0;

    return bFound - aFound;
  };

  return pools?.sort(sortPoolsForRoute);
};
